/**
 * Atlas PM · 用户管理 · 组织选择控件（P4.3）
 *
 * 数据源：GET /system/orgs 返回的树。
 *   - OrgSelect：单选下拉（历史控件；P7.4 后省管理员改用 OrgCheckList）
 *   - OrgCheckList：多选（P7.4：省管理员「所属组织」可多选，管多个省）
 *
 * 两者都把树**扁平化**后用缩进表达层级，避免自绘递归下拉的复杂度与可测性风险。
 */

import type { OrgNode } from './api';

export interface FlatOrg {
  id: string;
  name: string;
  code: string;
  level: number;
  depth: number;
}

/** 深度优先扁平化组织树 */
export function flattenOrgs(nodes: OrgNode[], depth = 0, out: FlatOrg[] = []): FlatOrg[] {
  for (const node of nodes) {
    out.push({ id: node.id, name: node.name, code: node.code, level: node.level, depth });
    if (node.children?.length) flattenOrgs(node.children, depth + 1, out);
  }
  return out;
}

export interface OrgSelectProps {
  orgs: OrgNode[];
  value: string | null;
  onChange(id: string | null): void;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/** 所属组织单选下拉 */
export function OrgSelect({
  orgs,
  value,
  onChange,
  disabled,
  id,
  className,
}: OrgSelectProps) {
  const flat = flattenOrgs(orgs);
  return (
    <select
      id={id}
      className={['p43-select', className].filter(Boolean).join(' ')}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">请选择组织…</option>
      {flat.map((o) => (
        <option key={o.id} value={o.id}>
          {'\u00A0'.repeat(o.depth * 3)}
          {o.name}
        </option>
      ))}
    </select>
  );
}

export interface OrgCheckListProps {
  orgs: OrgNode[];
  selected: string[];
  onToggle(id: string): void;
  disabled?: boolean;
  /** 容器 id（保留 id="p43-org" 约定，供既有自测脚本定位） */
  id?: string;
}

/** 数据范围涉及组织多选 */
export function OrgCheckList({ orgs, selected, onToggle, disabled, id }: OrgCheckListProps) {
  const flat = flattenOrgs(orgs);
  if (flat.length === 0) {
    return (
      <div id={id} className="p43-orglist p43-orglist--empty">
        暂无组织数据
      </div>
    );
  }
  return (
    <div id={id} className="p43-orglist">
      {flat.map((o) => {
        const checked = selected.includes(o.id);
        return (
          <label
            key={o.id}
            className={['p43-orgitem', checked ? 'on' : ''].join(' ')}
            style={{ paddingLeft: `${8 + o.depth * 14}px` }}
          >
            <input
              type="checkbox"
              className="p43-orgcheck"
              data-org-id={o.id}
              checked={checked}
              disabled={disabled}
              onChange={() => onToggle(o.id)}
            />
            <span className="p43-orgitem__name">{o.name}</span>
            <span className="p43-orgitem__code">{o.code}</span>
          </label>
        );
      })}
    </div>
  );
}
